/**
 * shared/lib/security-posture/tier-ladder/format.ts
 *
 * Task 8 (UI vocabulary consolidation, AC-9, spec module 01 §"UI vocabulary
 * consolidation — FOUR vocabularies (D-h, widened)"). ONE shared formatter
 * that every legacy status/gate copy site now renders through, retiring:
 *   1. Account status   — "starting_out"/"protected"/"well_protected"/
 *      "highly_resilient" (`../assessment.ts` `AccountStatusKey`)
 *   2. Session status    — "limited"/"standard"/"elevated"
 *      (`../assessment.ts` `SessionStatusKey`)
 *   3. Posture tier       — "Essential"/"Protected"/"Assured"/"Fortified"
 *      (`../score.ts` `TIER_ORDER`, display-only, ledger #110) — that whole
 *      band scale was DELETED with the score itself (AC-16, 2026-09-04);
 *      only its retirement remains recorded here
 *   4. Gate copy          — "Basic"/"Strong"/"Maximum" protection
 *      (`client/src/lib/terminology.ts` `PROTECTION_LEVELS`)
 *
 * ZERO ENFORCEMENT CHANGE (Task 8 scope): this module formats text only. It
 * does not compute, gate, or admit anything — every caller still derives its
 * own level number from its OWN existing thresholds/order, unchanged. This
 * mirrors the module 03 messaging stamp's identical "Level N of 4" shape
 * (AC-17) — the SAME two-function shape is reused there for message stamps,
 * not duplicated (that lane wires its own callers; this file only owns the
 * formatter, so both lanes can share it without either depending on the
 * other's call sites).
 *
 * LEVEL MAPPING (documented per vocabulary, honest about what does not fit):
 *   - Account status / posture tier were BOTH four-way scales driven off the
 *     identical 0/20/45/70 score thresholds — they always agreed, so both
 *     mapped 1:1 onto Level 1–4 in ascending order: starting_out/Essential=1,
 *     protected/Protected=2, well_protected/Assured=3,
 *     highly_resilient/Fortified=4. Only the ENFORCEMENT half survives
 *     (`accountStatusForTotal` in `../assessment.ts`, read by the org
 *     admission baseline over its own snapshot); the display half went with
 *     the score (AC-16).
 *   - Session status and the AAL-keyed gate copy are both THREE-way scales
 *     (no fourth rung exists in either — `sessionStatusForAal` and
 *     `PROTECTION_LEVELS` are both keyed by AAL1/AAL2/AAL3 only). Per the
 *     ladder table (module 01 §"The four-level ladder"): Level 1 = no
 *     passkey (AAL1/limited), Level 2 = one active passkey (AAL2/standard),
 *     Level 3 = key tap + strong proof, fresh (AAL3/elevated — dual-passkey
 *     or attested-hardware). Level 4 additionally requires an enrolled,
 *     recovery-ready guardian on the ACCOUNT (AC-66/"Notable collapse") — a
 *     fact neither AAL nor session status ever carried, so AAL3 maps to
 *     Level 3, never Level 4. Claiming Level 4 for a plain AAL3 session would
 *     be false assurance (Friction Rule clause 2): it would claim a guardian
 *     safety net that may not exist.
 */

/** The ladder is always expressed as a fraction of 4 (AC-9, AC-17) — never a
 *  bare number, so a reader who has never seen the ladder still reads it
 *  correctly as a fraction of a known maximum. */
export const LADDER_LEVEL_COUNT = 4;

/** The four rungs any of the retired vocabularies can resolve to for display. */
export type LadderDisplayLevel = 1 | 2 | 3 | 4;

/**
 * "Level N" — the bare rung name, with no total and no qualifier. NOT
 * user-facing on its own (fix round 1, ruling 6 + this file's own rule above:
 * a bare rung name still omits the "of 4"/"fresh" context that makes it
 * honest on screen) — exists ONLY so every other export below, and any
 * caller needing a short badge form (e.g. `terminology.ts`'s
 * `ProtectionLevelInfo.short`), derives the digit from ONE place instead of
 * hand-typing `"Level 1"`/`"Level 2"`/`"Level 3"` separately from the level
 * number passed to `levelOfFourLabel`/`levelFreshPhrase` — the exact
 * duplication that let a caller drift (wrong digit in one shape, right digit
 * in the other) without a compiler catching it.
 */
export function levelLabel(level: LadderDisplayLevel): string {
  return `Level ${level}`;
}

/**
 * "Level N of 4" — the one shared status-label shape (AC-9, AC-17). Every
 * legacy account-status, session-status, posture-tier, and message-stamp
 * label now renders through this, never a bare number and never one of the
 * four retired words.
 */
export function levelOfFourLabel(level: LadderDisplayLevel): string {
  return `${levelLabel(level)} of ${LADDER_LEVEL_COUNT}`;
}

/**
 * "Level N, fresh" — the bare gate-copy fragment (AC-9, master.md
 * §Simplicity: "unified into 'Level N, fresh' gate copy") every retired
 * "Basic"/"Strong"/"Maximum protection" gate now reads. Deliberately NOT the
 * "of 4" status-label shape above — this is the action-facing REQUIREMENT
 * phrase, not a status readout. "fresh" names the honesty constraint this
 * whole spec is built on (possession proven recently, never liveness) —
 * every caller keeps it, whether embedded in its own sentence shape (e.g.
 * "You need Level N, fresh to see this.") or via `levelGateSentence` below.
 */
export function levelFreshPhrase(level: LadderDisplayLevel): string {
  return `${levelLabel(level)}, fresh`;
}

/**
 * "This action needs Level N, fresh" — the canonical full gate-copy sentence
 * (AC-9) for callers with no existing sentence shape of their own to embed
 * `levelFreshPhrase` into.
 */
export function levelGateSentence(level: LadderDisplayLevel): string {
  return `This action needs ${levelFreshPhrase(level)}`;
}

/**
 * Owner ruling 2026-09-01 (PR #2989 walk-through): the ladder's four rungs
 * gain a plain-English descriptor name — reachable for someone reading a
 * headline level for the first time, not only a reader who already knows
 * "of 4" is a fraction of a known maximum.
 *
 * Reuse of two of these four words is DELIBERATE and owner-authorized: the
 * AC-9 vocabulary-consolidation sweep
 * (`tests/unit/security-tier-ladder/vocabulary-consolidation.test.ts`)
 * retired the COMPOUND gate-copy phrases built from two of them — those
 * exact phrases stay banned, unedited by this change. The owner explicitly
 * authorized reusing the bare words here because no real user has seen the
 * retired copy yet (pre-launch, CLAUDE.md "all users are test users") — there
 * is no legacy reading for the new bare-word usage to collide with.
 *
 * DISPLAY-LAYER ONLY, same as every other export in this file (see header):
 * this map feeds `levelOfFourDisplay` below and nothing else. It must never
 * back a WIRE value — `levelOfFourLabel`'s own plain "Level N of 4" string
 * stays the one every wire and gate-copy shape uses, unchanged. (Its former
 * wire consumer, `score.tier`, no longer exists — AC-16.)
 *
 * NAMING COLLISION ON RECORD (adversarial-review F8, 2026-09-02): Level 4's
 * "Guarded" is a plain-English word that ALSO names an unrelated component,
 * `Guarded` (`client/src/app/guards.tsx`, ~25 call sites: `<Guarded minAal=
 * "AAL2">…</Guarded>` route wrapping). A future retirement of this
 * descriptor set can NOT needle-sweep the bare word "Guarded" the way
 * `vocabulary-consolidation.test.ts` sweeps the OTHER retired vocabularies
 * (that gate's own header records the same class of objection for reused
 * bare words — see its "Fix round 1, ruling 2" note on why some retired
 * words stayed OUT of the banned list as false-positive-prone). Any future
 * sweep for this descriptor must use a needle scoped to the composed
 * "· Guarded" shape (or an AST/import-aware check), never the bare word.
 */
export const LEVEL_DESCRIPTORS: Record<LadderDisplayLevel, string> = {
  1: "Basic",
  2: "Everyday",
  3: "Strong",
  4: "Guarded",
};

/**
 * "Level N of 4 · <name>" — the hybrid headline shape the owner's standing
 * D12 ruling calls for (2026-09-01): the scale renders first so a reader who
 * has never seen the ladder still reads the fraction correctly, then the
 * middot, then the plain-English name for a reader who has. Composed from
 * `levelOfFourLabel` (not a re-typed literal), so the two can never drift
 * apart on the number — only on whether the name is appended.
 *
 * Headline STATUS surfaces only (what level an account or session is AT
 * right now) — this does not replace `levelOfFourLabel` at any call site
 * embedded mid-sentence, and it does not replace `levelGateSentence`'s
 * REQUIREMENT phrasing. Level 4's own honesty note
 * (`LEVEL_4_SAFETY_NET_NOTE`, `security-centre-copy.ts`) is unaffected by
 * this function — a caller rendering Level 4 through this formatter still
 * owes that note wherever it already rendered it.
 */
export function levelOfFourDisplay(level: LadderDisplayLevel): string {
  return `${levelOfFourLabel(level)} · ${LEVEL_DESCRIPTORS[level]}`;
}

/**
 * The optional-level-field spread every ladder denial body repeats: write
 * `{ [key]: level, [key]Label: levelOfFourDisplay(level) }` when `level` is
 * defined, nothing when it isn't. Moved here (fix round, adversarial review)
 * from `server/logic/security-tier-ladder/floor-enforcement.ts` — that file
 * and its sibling `role-ceiling.ts` are two independent gates on purpose
 * (`role-ceiling.ts`'s own header: "THE ONE THING THAT MAKES THIS NOT A COPY
 * OF `./floor-enforcement`"), and `role-ceiling.ts` importing a helper out of
 * `floor-enforcement.ts` was a runtime edge between them that neither header
 * intends. Both now import this shared, display-only helper instead. Wire
 * shape is unchanged: the emitted keys and values are exactly what each call
 * site wrote by hand.
 */
export function optionalLevelField<K extends string>(
  key: K,
  level: LadderDisplayLevel | undefined,
): { readonly [P in K]?: LadderDisplayLevel } & { readonly [P in `${K}Label`]?: string } {
  type Result = { readonly [P in K]?: LadderDisplayLevel } & {
    readonly [P in `${K}Label`]?: string;
  };
  if (level === undefined) return {} as Result;
  return { [key]: level, [`${key}Label`]: levelOfFourDisplay(level) } as Result;
}
